import { Alert, Button, Card, Form, Input, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Dispute = {
  id: string;
  status: string;
  reason: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  period_key?: string;
};

export default function FeeDisputeResolvePage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['education-fee-disputes'],
    queryFn: () => api.get<Dispute[]>('/education/fees/disputes'),
  });
  const dispute = (query.data?.data ?? []).find((row) => row.id === id);

  const resolve = useMutation({
    mutationFn: (values: { resolution_notes: string }) =>
      api.post(`/education/fees/disputes/${id}/resolve`, values),
    onSuccess: () => {
      message.success(t('feeDisputeResolved'));
      navigate('/education/fees/disputes');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('resolveFeeDispute')}
      description={t('feeDisputesIntro')}
      extra={<Button onClick={() => navigate('/education/fees/disputes')}>{t('backToFees')}</Button>}
    >
      <Card size="small" loading={query.isLoading} style={{ maxWidth: 560 }}>
        {!dispute ? (
          <Alert type="warning" showIcon message={t('unavailable', { status: '' })} />
        ) : dispute.status !== 'open' ? (
          <Alert type="info" showIcon message={t('disputeNotOpen', { defaultValue: 'Only an open dispute can be resolved.' })} />
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${dispute.admission_no || ''} — ${dispute.first_name || ''} ${dispute.last_name || ''} · ${dispute.period_key || ''} · ${dispute.reason}`}
            />
            <Form layout="vertical" onFinish={(values) => resolve.mutate(values)}>
              <Form.Item name="resolution_notes" label={t('resolutionNotes')} rules={[{ required: true }]}>
                <Input.TextArea rows={4} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={resolve.isPending}>
                {t('resolve')}
              </Button>
            </Form>
          </>
        )}
      </Card>
    </EducationPageChrome>
  );
}
